import React, { useEffect, useState } from 'react';
import Icon from '../components/Icon';
import { useBilling } from './BillingContext';
import PricingTable from './PricingTable';
import { formatMoney, formatLongDate } from './format';
import './billing.css';

const PACK_FOR = { generations: 'generations_100', videos: 'videos_10' };

// Headline and pitch for what the user just ran into, framed as what they get next
const copyFor = (reason, planName) => {
    if (reason.feature === 'premiumVideo') {
        return { icon: 'movie', title: 'Make videos that look shot, not generated', sub: 'The premium video model is part of Scale.' };
    }
    switch (reason.metric) {
        case 'generations':
            return {
                icon: 'auto_awesome',
                title: `You've created ${reason.limit} pieces of content this month`,
                sub: "Don't stop at a great start. Keep a full week of posts going, every week.",
            };
        case 'videos':
            return reason.limit > 0
                ? { icon: 'movie', title: `You've made all ${reason.limit} AI videos this month`, sub: 'Your hooks are working. Keep turning them into videos.' }
                : { icon: 'movie', title: 'Turn your best hooks into videos', sub: 'AI videos come with Growth and Scale.' };
        case 'products':
            return {
                icon: 'inventory_2',
                title: 'Market your next product',
                sub: `Your ${planName} plan covers ${reason.limit} product${reason.limit === 1 ? '' : 's'}. Growth covers 3 and Scale covers 10.`,
            };
        default:
            return { icon: 'rocket_launch', title: 'Get a full week of content, every week', sub: 'Planned, written and tracked for your customers. One new customer a month pays for it.' };
    }
};

const UpgradeModal = ({ reason, onClose }) => {
    const { account, usage, pricing, choosePlan, buyPack, busy, error } = useBilling();
    const [interval, setBillingInterval] = useState('year');
    const plan = account?.plan || reason.plan || 'free';
    const planName = pricing?.plans.find(p => p.id === plan)?.name || 'Free';

    useEffect(() => {
        const handleKey = (event) => event.key === 'Escape' && onClose();
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onClose]);

    const copy = copyFor(reason, planName);
    const needsScale = reason.feature === 'premiumVideo' || (reason.metric === 'products' && reason.limit >= 3);
    const plans = plan === 'free' ? ['growth', 'scale'] : plan === 'growth' ? ['scale'] : [];
    const recommended = plan === 'free' && !needsScale ? 'growth' : 'scale';
    const pack = pricing?.packs.find(p => p.id === PACK_FOR[reason.metric]);
    // Paying customers who run out mid-month get the top-up first
    const packFirst = plan !== 'free' && pack;

    const packOffer = pack && (
        <div className={`ls-pack-offer${packFirst ? ' is-primary' : ''}`}>
            <Icon name="add_circle" size={22} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{packFirst ? pack.outcome : 'Just need a few more?'}</div>
                <div className="ls-muted" style={{ fontSize: 13 }}>
                    {pack.name}, one-off. Never expires.
                </div>
            </div>
            <button
                type="button"
                className={`ls-btn ${packFirst ? 'ls-btn-primary' : 'ls-btn-secondary'}`}
                disabled={Boolean(busy)}
                onClick={() => buyPack(pack.id)}
            >
                {busy === `pack:${pack.id}` ? <span className="ls-spinner" /> : `Add for ${formatMoney(pack.price, pricing.currency)}`}
            </button>
        </div>
    );

    return (
        <div className="ls-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className={`ls-modal ls-upgrade${plans.length < 2 ? ' is-narrow' : ''}`} role="dialog" aria-modal="true" aria-labelledby="upgrade-title">
                <button type="button" className="ls-chip-remove ls-modal-close" aria-label="Close" onClick={onClose}>
                    <Icon name="close" size={20} />
                </button>
                <div className="ls-upgrade-head">
                    <div className="ls-upgrade-icon"><Icon name={copy.icon} size={24} /></div>
                    <h2 id="upgrade-title">{copy.title}</h2>
                    <p>{copy.sub}</p>
                </div>

                {error && <div className="ls-error" role="alert"><Icon name="error" />{error}</div>}

                {packFirst && packOffer}

                {plans.length > 0 && (
                    <PricingTable
                        pricing={pricing}
                        interval={interval}
                        onIntervalChange={setBillingInterval}
                        onSelect={choosePlan}
                        account={account}
                        busy={busy}
                        plans={plans}
                        recommended={recommended}
                    />
                )}

                {!packFirst && packOffer}

                {plans.length === 0 && !pack && (
                    <div className="ls-notice">
                        You're on our biggest plan. Need more? Email <a href="mailto:hello@light-speed-ai.com">hello@light-speed-ai.com</a> and we'll sort you out.
                    </div>
                )}

                {['generations', 'videos'].includes(reason.metric) && reason.limit > 0 && usage?.resetsAt && (
                    <div className="ls-muted" style={{ fontSize: 13, textAlign: 'center' }}>
                        Or wait: your {planName} allowance resets on {formatLongDate(usage.resetsAt)}.
                    </div>
                )}
            </div>
        </div>
    );
};

export default UpgradeModal;
